<template>
  <div class="tender-container">
    <TenderHead
      ref="tenderEditHead"
      :tenderGeneral="tender.general"
    >
      <div
        slot="status"
        class="float-left"
      >
        <div class="tender-status tender-bid-status">
          {{ statusText }}
        </div>
        <HKLGrayHint
          v-if="showUpdatorInfo"
          class="text-field tender-bid-updated-by"
        >
          {{ $t('lastUpdatedBy', [currentBid.vendorUpdatorName, dateUtil.format(currentBid.vendorUpdatedAt, 'DD MMM YY HH:mm')]) }}
        </HKLGrayHint>
      </div>
      <div class="row">
        <button
          v-if="!showQuery"
          type="button"
          class="btn btn--darkblue"
          @click="exportPDF(exportOptions[0])"
        >{{ $t('action.exportBid') }}</button>
        <ExportPdfWithOptions
          v-if="showQuery"
          :options="exportOptions"
          @export:pdf="val => exportPDF(val)"
        />
        <button
          v-if="isInvited"
          type="button"
          class="btn btn--orange"
          @click="startBid()"
        >{{ $t('action.startBid') }}</button>
        <div v-if="editView">
          <button
            type="button"
            class="btn btn--orange"
            @click="save()"
          >{{ $t('action.save') }}</button>
          <button
            type="button"
            class="btn btn--orange"
            @click="openSubmitModal()"
          >{{ $t('action.submit') }}</button>
          <submit-bid ref="subitBidModal" />
          <WarnTenderUpdate ref="warnTenderUpdateModal" />
        </div>
        <button
          v-if="showWithDrawn"
          type="button"
          class="btn btn--orange"
          @click="openWithdrawModal()"
        > {{ $t('action.withdraw') }} </button>
      </div>
    </TenderHead>

    <RecentAddendum
      v-if="general.version"
      :tenderId="general.id"
      :isBid="true"
      :includePendingChange="!currentTender.general.hasPendingAddendum"
      :addendumVersion="addendumVersion"
    />

    <TenderTabsContainer
      :isTender="isTender"
      :tabsInvalid="tabsInvalid"
      :showQueryTab="showQuery"
    >
      <div class="row">
        <button
          v-if="!showQuery"
          type="button"
          class="btn btn--darkblue tender-sticky-button mini-btn"
          @click="exportPDF(exportOptions[0])"
        >{{ $t('action.exportBid') }}</button>
        <export-pdf-with-options
          v-if="showQuery"
          :options="exportOptions"
          @export:pdf="val => exportPDF(val)"
        />
        <div
          v-if="editView"
          style="margin-left: 5px;"
        >
          <button
            type="button"
            class="btn btn--orange tender-sticky-button mini-btn"
            @click="save()"
          >{{ $t('action.save') }}</button>
          <button
            type="button"
            class="btn btn--orange tender-sticky-button mini-btn"
            @click="openSubmitModal()"
          >{{ $t('action.submit') }}</button>
        </div>
        <button
          v-if="showWithDrawn"
          type="button"
          class="btn btn--orange tender-sticky-button mini-btn"
          @click="openWithdrawModal()"
        > {{ $t('action.withdraw') }} </button>
      </div>
      <div slot="general">
        <General
          :general.sync="general"
          :isContractor="true"
          :tenderBidId="tenderBidId"
        />
        <DeclineTender
          v-if="editView || isInvited"
          :tenderBidId="tenderBidId"
        />
      </div>
      <ScopeOfWork
        slot="scopeOfWork"
        :items="general.scopeOfWorks"
      />
      <ItemsServicesBid
        slot="itemServices"
        ref="itemsServicesTab"
        :itemServices="service"
        :editView="editView"
        :bidService.sync="bidService"
        :totalFee="bidService.totalFee"
        @update:totalFee="updateTotalFee"
      />
      <Attachments
        slot="fileUpload"
        :attachment.sync="attachment"
        :tenderId="general.id"
        :tenderBidId="tenderBidId"
        :editView="editView"
        :attachmentDesc="attachment.description"
        :zipPrefix="zipPrefix"
      />
      <TermsConditions
        slot="termsConditions"
        ref="termsConditionsTab"
        :isContractorView="true"
        :tenderId="general.id"
        :zipName="zipNames.terms"
        :editView="editView"
        :totalFee="bidService.totalFee"
        :tncInfo.sync="tncInfo"
        :isTender="isTender"
      />
      <TenderQueryAccordion
        v-if="showQuery"
        slot="query"
      />
    </TenderTabsContainer>
    <Modal
      ref="withdrawBid"
      modalId="withdrawBid"
    >
      <div slot="content">
        <p class="modal-pure-text">
          {{ $t('vendor.bid.withdraw.popupTitle') }}
        </p>
      </div>
      <div slot="btns">
        <button
          type="button"
          class="btn btn--darkblue"
          aria-label="Close reveal"
          @click="closeWithdrawModal()"
        >{{ $t('action.cancel') }}</button>
        <button
          type="submit"
          class="btn btn--orange"
          @click="withdrawn()"
        >{{ $t('action.yesContinue') }}</button>
      </div>
    </Modal>
  </div>
</template>

<script>
import lodash from 'lodash'
import { mapActions, mapMutations, mapGetters } from 'vuex'

import Attachments from '@/modules/vendors/bids/Attachments'
import DeclineTender from '@/modules/vendors/bids/DeclineTender'
import ExportPdfWithOptions from '@/controls/ExportPdfWithOptions'
import General from '@/modules/tenders/view/General'
import ItemsServicesBid from '@/modules/tenders/view/ItemsServicesBid'
import Modal from '@/controls/Modal'
import RecentAddendum from '@/modules/tenders/addendum/RecentAddendum'
import ScopeOfWork from '@/modules/tenders/view/ScopeOfWork'
import SubmitBid from '@/modules/vendors/bids/SubmitBid'
import TenderHead from '@/modules/tenders/TenderHead'
import TenderTabsContainer from '@/modules/tenders/TenderTabsContainer'
import TenderQueryAccordion from '@/modules/tenders/queries/TenderQueryAccordion'
import TermsConditions from '@/modules/tenders/TermsConditions'
import WarnTenderUpdate from '@/modules/vendors/bids/WarnTenderUpdate'

import bidMixin from './bidMixin'
import dateUtil from '@/utils/dateUtil'
import router from '@/router'
import mutationTypes from '@/store/mutation-types'
import navigation from '@/utils/navigation'
import notification from '@/utils/notification'
import { EmailTypes, ActionTypes } from '@/utils/consts'
import { fusionInputCheckUtil } from '@/plugin/FusionInputCheck'
import { getBidById, parseVendorBidStatus, getQtyAddendumedServiceLineItemsFromCurTender, exportContractorPDF } from '@/api/bids/request'
import { getZipNames } from '@/store/modules/tenders'
import { sendEmail } from '@/api/postman/request'
import { updateDescription, getAttachments, updateOrder } from '@/api/docs/request'
import { utils as formUtils } from '@/plugin/InputChange'
import { vendorBidStatus, tenderStatus, queryStatus } from '@/utils/locale'
import HKLGrayHint from '@/controls/HKLGrayHint.vue'
export default {
  name: 'TendersBid',
  components: {
    HKLGrayHint,
    General,
    ItemsServicesBid,
    TenderHead,
    TermsConditions,
    ScopeOfWork,
    DeclineTender,
    SubmitBid,
    Attachments,
    TenderTabsContainer,
    WarnTenderUpdate,
    RecentAddendum,
    ExportPdfWithOptions,
    TenderQueryAccordion,
    Modal,
  },
  mixins: [bidMixin],
  data () {
    const exportOptions = [
      `${this.$i18n.t('bid')} ${this.$i18n.t('summary.name')}`,
      `${this.$i18n.t('tender.query.name')} ${this.$i18n.t('summary.name')}`,
    ]
    return {
      tenderBidId: null,
      tender: {
        general: { tenderType: 'Tender' },
      },
      general: {},
      service: [],
      attachment: {
        description: '',
        files: [],
      },

      bidService: {
        totalFee: '',
      },
      tncInfo: {},
      isTender: true,

      tabsInvalid: {
        generalInvalid: false,
        sectionInvalid: false,
        termsInvalid: false,
      },
      tenderStatus,
      exportOptions,
      dateUtil,
    }
  },
  computed: {
    ...mapGetters('user', ['currentUser', 'currentUserContact']),
    ...mapGetters('tenders', [
      'currentTender',
      'isAddendum',
      'currentBid',
    ]),
    isInvited () {
      return this.currentBid.status === vendorBidStatus.INVITED
    },
    isDraft () {
      return this.currentBid.status === vendorBidStatus.DRAFT
    },
    editView () {
      return this.isDraft
    },
    showWithDrawn () {
      return [vendorBidStatus.SUBMITTED, vendorBidStatus.DECLINED].includes(this.currentBid.status) && !this.isTenderStatusAfterIssue
    },
    statusText () {
      return parseVendorBidStatus(this.currentTender, this.currentBid)
    },
    zipNames () {
      return getZipNames(this.tender)
    },
    zipPrefix () {
      const prNumber = deep(this.tender, 'general.prNumber')
      return prNumber ? `Tender_${prNumber}` : 'Tender'
    },
    showQuery () {
      return (this.currentBid && this.currentBid.queries && this.currentBid.queries.length > 0)
    },
    addendumVersion () {
      return (this.currentBid.status === vendorBidStatus.DECLINED && this.currentBid.declineVersion) || 0
    },
    isTenderStatusAfterIssue () {
      return [tenderStatus.AWARDED, tenderStatus.REVIEW, tenderStatus.READY, tenderStatus.REGRETTED].includes(this.currentTender.general.status)
    },
    hasOpenQuery () {
      let hasOpenQuery = this.showQuery
      if (hasOpenQuery) {
        hasOpenQuery = this.currentBid.queries.some(query => query.status === queryStatus.ISSUED)
        if (hasOpenQuery) {
          document.querySelector('#tab-query-label').click()
          document.querySelector('#tab-query-label').click() // click twice to make sure the autosize auto-expand
        }
      }
      return hasOpenQuery
    },
    firstIssuedDate () {
      let firstIssuedDate = deep(this.currentTender, 'general.issuedDate')
      if ((deep(this.currentTender, 'general.version') || 0) > 0 && deep(this.currentTender, 'firstIssuedVersion.data')) {
        firstIssuedDate = deep(this.currentTender, 'firstIssuedVersion.data.issuedDate')
      }
      return firstIssuedDate ? dateUtil.formatDate(firstIssuedDate, 'MMM dd') : null
    },
    showUpdatorInfo () {
      return this.currentBid && this.currentBid.vendorUpdatedAt && !this.isTenderStatusAfterIssue
    },
  },
  async mounted () {
    await this.initTenderBid()
    const routerQuery = this.$router.currentRoute.query
    const currentTenderStatus = deep(this.tender, 'general.status')
    const existInAward = tenderStatus === tenderStatus.AWARDED && (Object.keys(routerQuery) && routerQuery.prev !== 'vendorProjectDetail')
    const existAfterTenderIssue = !this.hasOpenQuery && [tenderStatus.REVIEW, tenderStatus.READY, tenderStatus.REGRETTED].includes(currentTenderStatus)
    if (existAfterTenderIssue || existInAward) {
      router.push({ name: 'vendorHome' })
      return
    }
    // Compatible with vuex state
    await this.populateCurrentTender()
    if (this.currentBid && this.currentBid.status === vendorBidStatus.DECLINED) {
      await this.loadDeclinedRevision()
    } else {
      await this.loadRecentRevision({ loadPendingAddendum: false, hasPendingAddendum: this.general.hasPendingAddendum })
    }
  },
  methods: {
    ...mapMutations('tenders', {
      updateTenderPathField: mutationTypes.TENDER_UPDATE_PATH_FIELD,
      setCurrentBid: mutationTypes.TENDER_SET_CURRENT_BID,
    }),
    ...mapActions('tenders', [
      'loadDeclinedRevision',
      'loadRecentRevision',
      'submitBid',
      'submitBidStatus',
      'refreshCurrentBid',
      'setTermsVersion',
    ]),
    updateTotalFee (val) {
      this.$set(this.bidService, 'totalFee', val)
    },
    async exportPDF (type) {
      if (this.editView) {
        await this.save(false)
      }

      const vendorFiles = await getAttachments(this.tenderBidId) || []
      const engineerFiles = lodash.orderBy(
        lodash.get(this.currentTender, 'general.documents', []),
        [({metaData}) => metaData && metaData.category ? -1 : 1, 'order'],
      )
      const request = lodash.cloneDeep({
        general: {
          ...this.general,
          documents: engineerFiles,
          firstIssuedDate: this.firstIssuedDate,
        },
        itemServices: this.service,
        attachments: { files: vendorFiles, description: this.attachment.description },
        tncInfo: this.tncInfo,
        bid: this.currentBid,
        issueDateArr: this.issueDateArr,
      })
      await exportContractorPDF(type, request, this.bidService)
    },

    getResponseData (response) {
      if (!response) {
        notification.fireNotification(false, 'Invalid response from server, please check with the server administrator')
        return ''
      }
      const data = response.data.data
      return data
    },
    async initTenderBid () {
      const tenderBidId = router.currentRoute.params.id
      this.tenderBidId = tenderBidId
      if (!this.tenderBidId) return
      const response = await getBidById(tenderBidId)
      if (response) {
        if (response.tender) {
          this.general = Object.assign(response.tender)
          this.tender.general = this.general
          this.attachment.description = this.tender.general.attachmentDescription
          this.isTender = !(this.general.tenderType === 'ITQ')
        }
        if (this.general.service) {
          this.tender.service = this.general.service
          this.service = this.tender.service
        }
        this.updateTenderPathField({
          path: ['general'],
          newValue: lodash.cloneDeep(this.general),
        })
        this.setTermsVersion({termsVersion: this.general.termsVersion})
        if (response.bid) {
          const bid = { ...this.currentBid, ...response.bid }
          if (response.bid.content) {
            this.bidService = lodash.cloneDeep(response.bid.content.service) || this.bidService
            this.tncInfo = response.bid.content.tncInfo || {}
            if (this.currentUserContact && this.currentUserContact.name) {
              this.tncInfo.companyName = this.currentUserContact.name
            }
          }
          this.setCurrentBid({ bid: lodash.cloneDeep(bid) })
        }
      }
    },
    async saveAttachment () {
      await Promise.all(
        this.attachment.files.map(async (file, index) => {
          file.order = index
          await updateOrder(file.id, index)
          if (file.description) {
            await updateDescription(file)
          }
          return file
        }),
      )
    },
    openWithdrawModal () {
      this.$refs.withdrawBid.open()
    },

    closeWithdrawModal () {
      this.$refs.withdrawBid.close()
    },

    clearErrorClass () {
      this.$refs.itemsServicesTab.clearErrorClasses()
      this.$refs.termsConditionsTab.clearErrorClasses()
    },

    validate () {
      fusionInputCheckUtil.validateAllFields({allowEmpty: 'partial'})
      this.tabsInvalid.sectionInvalid = this.$refs.itemsServicesTab.validate() || !fusionInputCheckUtil.isAllFieldsValid('#items-services-form')
      this.tabsInvalid.termsInvalid = this.$refs.termsConditionsTab.validate()
      return (!(this.tabsInvalid.sectionInvalid || this.tabsInvalid.termsInvalid) && fusionInputCheckUtil.isAllFieldsValid())
    },

    async tenderHasUpdated () {
      const response = await getBidById(this.tenderBidId)
      if (response && response.tender) {
        const status = response.tender.status
        const version = response.tender.version
        return [status === tenderStatus.ISSUED && version !== this.general.version, response.tender]
      }
      return [true, null]
    },

    async resetCurBidQtyAfterAddeudum (tender) {
      const qtyUpdatedLineIds = ((await getQtyAddendumedServiceLineItemsFromCurTender(tender.id, tender)) || []).map(rec => rec.id)
      const curQtys = (deep(this.bidService, 'bidItemQtys') || {})
      deep(this.bidService, 'bidItemQtys', Object.keys(curQtys).reduce((result, id) => {
        if (!qtyUpdatedLineIds.includes(id)) {
          result[id] = curQtys[id]
        }
        return result
      }, {}))
    },

    async startBid () {
      await this.submitBidStatus({
        bidId: this.tenderBidId,
        request: { status: vendorBidStatus.DRAFT },
        action: ActionTypes.tenders.bid.start,
      })
      await this.refreshCurrentBid(this.tenderBidId)
    },
    async withdrawn () {
      try {
        const action = ActionTypes.tenders.bid.withdraw
        await this.submitBidStatus({
          bidId: this.tenderBidId,
          request: { status: vendorBidStatus.DRAFT },
          action,
        })
        await sendEmail(EmailTypes.tenders.withdrawBid)
        await this.refreshCurrentBid(this.tenderBidId)
      } catch (err) {
        notification.handleResponseAlert(err.response, 'tenders')
      }
      this.closeWithdrawModal()
      this.$router.go(0)
    },

    async save (showMsg = true) {
      fusionInputCheckUtil.resetAllFields()
      this.tabsInvalid.sectionInvalid = false
      this.tabsInvalid.termsInvalid = false
      this.clearErrorClass()
      try {
        this.$refs.itemsServicesTab.emitBidServiceDate()
        const [tenderHasUpdated, updatedTender] = await this.tenderHasUpdated()
        if (tenderHasUpdated) {
          await this.resetCurBidQtyAfterAddeudum(updatedTender)
        }
        await this.submitBid({
          bidId: this.tenderBidId,
          request: {
            status: vendorBidStatus.DRAFT,
            content: { service: { ...this.bidService }, tncInfo: { ...this.tncInfo } },
          },
        })
        await this.saveAttachment()
        if (tenderHasUpdated) {
          this.$refs.warnTenderUpdateModal.openAlertTenderUpdateModal()
          return
        }
        await this.refreshCurrentBid(this.tenderBidId)
        formUtils.resetDirty()
        if (showMsg) {
          notification.fireNotification(true, this.$i18n.t('success'))
        }
      } catch (err) {
        notification.handleResponseAlert(err.response)
      }
    },

    openSubmitModal () {
      if (!this.validate()) {
        return
      }
      this.$refs.subitBidModal.openAlertSubmitModal()
    },

    async submit () {
      this.$root.$children[0].$refs.loadingLayer.$emit('startLoading')
      try {
        this.$refs.itemsServicesTab.emitBidServiceDate()
        const [tenderHasUpdated, updatedTender] = await this.tenderHasUpdated()
        if (tenderHasUpdated) {
          await this.resetCurBidQtyAfterAddeudum(updatedTender)
        }
        await this.submitBid({
          bidId: this.tenderBidId,
          request: {
            status: vendorBidStatus.DRAFT,
            content: { service: { ...this.bidService }, tncInfo: { ...this.tncInfo } },
          },
        })
        await this.saveAttachment()
        if (tenderHasUpdated) {
          this.$refs.warnTenderUpdateModal.openAlertTenderUpdateModal()
          return
        }
        const action = this.isAddendum > 1
          ? ActionTypes.tenders.addendum.bid.submit
          : ActionTypes.tenders.bid.submit
        await this.submitBidStatus({
          bidId: this.tenderBidId,
          request: { status: vendorBidStatus.SUBMITTED },
          action,
        })
        formUtils.resetDirty()
        await this.emailNotification()
        navigation.toHome()
      } catch (err) {
        notification.handleResponseError(err, 'tenders')
      } finally {
        this.$root.$children[0].$refs.loadingLayer.$emit('stopLoading')
      }
    },
    async emailNotification () {
      try {
        this.$root.$children[0].$refs.loadingLayer.$emit('startLoading')
        const response = await getBidById(this.tenderBidId)
        if (response && response.bid && response.bid.respondedOn) {
          const metaData = {
            version: response.bid.version,
            respondedOn: response.bid.respondedOn,
            isSubmission: true,
          }
          await sendEmail(EmailTypes.tenders.submitBid, metaData)
        }
      } finally {
        this.$root.$children[0].$refs.loadingLayer.$emit('stopLoading')
      }
    },
    async loadFiles () {
      const files = await getAttachments(this.general.id)
      return files
    },
    async populateCurrentTender () {
      if (!this.general.documents) {
        this.updateTenderPathField({
          path: ['general', 'documents'],
          newValue: await this.loadFiles(),
        })
      }
      this.updateTenderPathField({
        path: ['service'],
        newValue: lodash.cloneDeep(this.service),
      })
      this.updateTenderPathField({
        path: ['scopeOfWorks'],
        newValue: lodash.cloneDeep(this.scopeOfWorks),
      })
      this.updateTenderPathField({
        path: ['firstIssuedVersion'],
        newValue: lodash.cloneDeep(this.general.firstIssuedVersion),
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~styles/tender/view";
@import "~styles/tabsContainer";
@import "~styles/popup";
@import "~styles/stickyBtn";

.tender-head .btn {
  margin-left: 12px;
}

input.is-invalid-input {
  background-color: #f9ecea!important;
}

.tender-bid-status, .tender-bid-updated-by {
  display: inline-block;
  vertical-align: middle;
}
</style>
